import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getGraphQLEndpoint } from '../utils/graphqlConfig';

export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  authProvider: string;
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginMfaChallenge {
  mfaToken: string;
  maskedEmail: string;
}

export interface LoginResult {
  success: boolean;
  requiresMfa?: boolean;
  mfaToken?: string;
  maskedEmail?: string;
  errorMessage?: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pendingMfaChallenge: LoginMfaChallenge | null;
  clearPendingMfaChallenge: () => void;
  changePassword: (newPassword: string) => Promise<{ success: boolean; errorMessage?: string }>;
  login: (credentials: LoginCredentials, onSuccess?: () => void) => Promise<LoginResult>;
  verifyLoginMfa: (mfaToken: string, code: string, onSuccess?: () => void) => Promise<{ success: boolean; errorMessage?: string }>;
  loginWithEntra: (code: string, onSuccess?: () => void) => Promise<boolean>;
  logout: (onSuccess?: () => void) => Promise<void>;
  refreshAuth: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const GRAPHQL_URL = getGraphQLEndpoint();

const ME_QUERY = `query {
  me {
    id
    email
    username
    firstName
    lastName
    role
    authProvider
    isActive
    emailVerified
    lastLoginAt
    createdAt
    updatedAt
  }
}`;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingMfaChallenge, setPendingMfaChallenge] = useState<LoginMfaChallenge | null>(null);

  const clearPendingMfaChallenge = () => setPendingMfaChallenge(null);

  const makeGraphQLRequest = async (query: string, variables?: Record<string, unknown>) => {
    const response = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 429) {
        console.warn('Rate limited by server, waiting before retry...');
        await new Promise(resolve => setTimeout(resolve, 5000));
        throw new Error(`HTTP ${response.status}: Rate limited - too many requests`);
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors[0].message || 'GraphQL error');
    }

    return result.data;
  };

  const loadUserProfile = async (): Promise<boolean> => {
    try {
      const data = await makeGraphQLRequest(ME_QUERY);

      if (data.me) {
        setUser(data.me);
        return true;
      }

      setUser(null);
      return false;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('401') || message.includes('Unauthorized') || message.includes('Authentication required')) {
        console.debug('User profile request unauthorized');
      } else {
        console.error('Failed to load user profile:', err);
      }
      return false;
    }
  };

  const migrateLegacyAuthStorage = async (): Promise<string | null> => {
    let legacyRefreshToken: string | null = null;

    try {
      const authTokensRaw = localStorage.getItem('auth_tokens');
      if (authTokensRaw) {
        const parsed = JSON.parse(authTokensRaw) as { refreshToken?: string };
        legacyRefreshToken = parsed.refreshToken ?? null;
      }
      if (!legacyRefreshToken) {
        legacyRefreshToken = localStorage.getItem('authToken');
      }
    } catch {
      // corrupt JSON — fall through to clear
    }

    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('auth-storage');

    if (legacyRefreshToken) {
      try {
        await makeGraphQLRequest(
          `mutation MigrateSession($refreshToken: String!) {
            refreshToken(refreshToken: $refreshToken) {
              success
            }
          }`,
          { refreshToken: legacyRefreshToken }
        );
      } catch (handoffError) {
        console.debug('Legacy session handoff failed:', handoffError);
      }
    }

    return legacyRefreshToken;
  };

  const tryRefreshFromCookies = async (): Promise<boolean> => {
    try {
      const data = await makeGraphQLRequest(
        `mutation {
          refreshToken {
            success
          }
        }`
      );

      if (!data.refreshToken?.success) {
        return false;
      }

      return loadUserProfile();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('Session validation failed')) {
        console.debug('Session validation failed - clearing authentication state');
        setUser(null);
      } else {
        console.debug('Failed to refresh from cookies:', err);
      }
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const legacyRefreshToken = await migrateLegacyAuthStorage();

      let profileLoaded = await loadUserProfile();

      if (!profileLoaded) {
        if (legacyRefreshToken) {
          profileLoaded = await loadUserProfile();
        }
        if (!profileLoaded) {
          const refreshed = await tryRefreshFromCookies();
          if (refreshed) {
            profileLoaded = true;
          } else {
            console.debug('No existing authentication found - user needs to log in');
          }
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials, onSuccess?: () => void): Promise<LoginResult> => {
    setError(null);

    try {
      const data = await makeGraphQLRequest(
        `mutation Login($input: LoginInput!) {
          login(input: $input) {
            requiresMfa
            mfaToken
            maskedEmail
            user {
              id
              email
              username
              firstName
              lastName
              role
              authProvider
              isActive
              emailVerified
              lastLoginAt
              createdAt
              updatedAt
            }
          }
        }`,
        { input: credentials }
      );

      const loginResult = data.login;

      if (loginResult.requiresMfa) {
        const challenge = {
          mfaToken: loginResult.mfaToken,
          maskedEmail: loginResult.maskedEmail || '',
        };
        setPendingMfaChallenge(challenge);
        return {
          success: true,
          requiresMfa: true,
          mfaToken: challenge.mfaToken,
          maskedEmail: challenge.maskedEmail,
        };
      }

      clearPendingMfaChallenge();
      setUser(loginResult.user);

      if (onSuccess) {
        onSuccess();
      }

      return { success: true };
    } catch (err) {
      console.error('Login failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      return { success: false, errorMessage };
    }
  };

  const verifyLoginMfa = async (mfaToken: string, code: string, onSuccess?: () => void) => {
    setError(null);

    try {
      const data = await makeGraphQLRequest(
        `mutation VerifyLoginMfa($input: VerifyLoginMfaInput!) {
          verifyLoginMfa(input: $input) {
            requiresMfa
            user {
              id
              email
              username
              firstName
              lastName
              role
              authProvider
              isActive
              emailVerified
              lastLoginAt
              createdAt
              updatedAt
            }
          }
        }`,
        { input: { mfaToken, code } }
      );

      setUser(data.verifyLoginMfa.user);
      clearPendingMfaChallenge();

      if (onSuccess) {
        onSuccess();
      }

      return { success: true };
    } catch (err) {
      console.error('MFA verification failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      setError(errorMessage);
      return { success: false, errorMessage };
    }
  };

  const loginWithEntra = async (code: string, onSuccess?: () => void) => {
    setError(null);

    try {
      const data = await makeGraphQLRequest(
        `mutation LoginWithEntra($code: String!) {
          loginWithEntra(code: $code) {
            user {
              id
              email
              username
              firstName
              lastName
              role
              authProvider
              isActive
              emailVerified
              lastLoginAt
              createdAt
              updatedAt
            }
          }
        }`,
        { code }
      );

      setUser(data.loginWithEntra.user);

      if (onSuccess) {
        onSuccess();
      }

      return true;
    } catch (err) {
      console.error('Entra ID login failed:', err);
      setError(err instanceof Error ? err.message : 'Entra ID login failed');
      return false;
    }
  };

  const logout = async (onSuccess?: () => void) => {
    try {
      await makeGraphQLRequest(`mutation { logout }`);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      clearPendingMfaChallenge();

      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const refreshAuth = async () => {
    await loadUserProfile();
  };

  const changePassword = async (newPassword: string) => {
    if (!user) {
      return { success: false, errorMessage: 'You must be signed in to change your password.' };
    }

    try {
      await makeGraphQLRequest(
        `mutation ChangePassword($input: ChangePasswordInput!) {
          changePassword(input: $input) {
            success
          }
        }`,
        { input: { newPassword } }
      );

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change password';
      return { success: false, errorMessage };
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    pendingMfaChallenge,
    clearPendingMfaChallenge,
    changePassword,
    login,
    verifyLoginMfa,
    loginWithEntra,
    logout,
    refreshAuth,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
